// Cell value introspection: format sniffing (JSON / XML-HTML / image / hex /
// plain text) for the cell value viewer, plus a small LCS-line diff used for
// "diff this cell against the one I picked." Kept side-effect-free + dep-free
// so a node test can exercise it directly.

// ── Format sniffing ─────────────────────────────────────────────────────────

// True if `s` parses cleanly as JSON of an OBJECT or ARRAY (not just a bare
// number/string/boolean — those are technically valid JSON but the viewer's
// JSON tab wouldn't add anything). Returns the parsed value too so callers
// don't re-parse.
export function tryJSON(s) {
  if (typeof s !== 'string') return null;
  const t = s.trim();
  if (!t || (t[0] !== '{' && t[0] !== '[')) return null;
  try { return { value: JSON.parse(t) }; } catch (_) { return null; }
}

// XML/HTML if the trimmed value starts with `<` and looks tag-like. Cheap; the
// viewer just renders it as text with a syntax-aware monospace style.
export function looksLikeMarkup(s) {
  if (typeof s !== 'string') return false;
  const t = s.trim();
  return /^<[?!a-zA-Z][\s\S]*>/.test(t);
}

// Inspect the first few bytes of a Uint8Array (or string interpretable as
// bytes) for a known image magic number. Returns the MIME type or null.
const IMAGE_SIGS = [
  { mime: 'image/png',  hdr: [0x89, 0x50, 0x4e, 0x47] },
  { mime: 'image/jpeg', hdr: [0xff, 0xd8, 0xff] },
  { mime: 'image/gif',  hdr: [0x47, 0x49, 0x46, 0x38] },
  { mime: 'image/webp', hdr: [0x52, 0x49, 0x46, 0x46] }, // followed by ...WEBP (we check both)
  { mime: 'image/bmp',  hdr: [0x42, 0x4d] },
];

export function imageMimeOf(bytes) {
  if (!bytes || !bytes.length) return null;
  outer: for (const sig of IMAGE_SIGS) {
    if (bytes.length < sig.hdr.length) continue;
    for (let i = 0; i < sig.hdr.length; i++) if (bytes[i] !== sig.hdr[i]) continue outer;
    if (sig.mime === 'image/webp') { // also need "WEBP" at offset 8
      if (bytes.length < 12 || bytes[8] !== 0x57 || bytes[9] !== 0x45 ||
          bytes[10] !== 0x42 || bytes[11] !== 0x50) continue;
    }
    return sig.mime;
  }
  return null;
}

// A value is "binary-ish" if it has any non-printable bytes that aren't
// whitespace — heuristic for switching to hex by default.
export function looksBinary(s) {
  if (typeof s !== 'string') return false;
  // Sample the first 256 chars — enough to spot binary cheaply.
  const n = Math.min(s.length, 256);
  let bad = 0;
  for (let i = 0; i < n; i++) {
    const c = s.charCodeAt(i);
    // Allow tab/lf/cr; flag any other control char or DEL.
    if (c < 32 && c !== 9 && c !== 10 && c !== 13) bad++;
    else if (c === 127) bad++;
  }
  return bad / n > 0.05; // > 5% non-text
}

// Bytes → hex dump (16/row, with ASCII gutter). Capped to avoid huge strings.
export function hexDump(bytes, cap = 4096) {
  const n = Math.min(bytes.length, cap);
  const lines = [];
  for (let off = 0; off < n; off += 16) {
    const chunk = bytes.slice(off, Math.min(off + 16, n));
    const hex = [...chunk].map((b) => b.toString(16).padStart(2, '0')).join(' ');
    const ascii = [...chunk].map((b) => (b >= 32 && b < 127 ? String.fromCharCode(b) : '.')).join('');
    lines.push(off.toString(16).padStart(8, '0') + '  ' + hex.padEnd(48, ' ') + '  ' + ascii);
  }
  if (bytes.length > cap) lines.push(`… ${bytes.length - cap} bytes truncated`);
  return lines.join('\n');
}

// String → Uint8Array; treats the string as a sequence of byte-valued
// characters (what MySQL hands back for BLOBs over the text protocol).
export function stringToBytes(s) {
  if (typeof s !== 'string') return new Uint8Array(0);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
  return out;
}

// Auto-pick the initial viewer tab. Order matters — image beats JSON beats
// markup beats binary beats text.
export function detectFormat(value) {
  if (value == null) return 'null';
  const s = String(value);
  const bytes = stringToBytes(s);
  if (imageMimeOf(bytes)) return 'image';
  if (tryJSON(s)) return 'json';
  if (looksLikeMarkup(s)) return 'markup';
  if (looksBinary(s)) return 'hex';
  return 'text';
}

// ── Line endings through a <textarea> ───────────────────────────────────────
// A textarea's value always reports line breaks as LF, so editing a CRLF value
// in one silently rewrote every CR. restoreLineEndings(edited, original) undoes
// that: text equal to the original apart from line endings IS the original
// (no change at all), and an edit of a value whose breaks were all CRLF gets
// CRLF breaks back.
export function restoreLineEndings(edited, original) {
  if (typeof edited !== 'string' || typeof original !== 'string' || !original.includes('\r')) return edited;
  const lf = original.replace(/\r\n?/g, '\n');
  if (edited === lf) return original;
  const allCrlf = !/\r(?!\n)|(^|[^\r])\n/.test(original);
  return allCrlf ? edited.replace(/\r?\n/g, '\r\n') : edited;
}

// ── LCS line diff ───────────────────────────────────────────────────────────
// Classic Hunt–McIlroy / Myers-friendly LCS over LINES — small (~30 lines),
// O(n·m) memory, fine for cell-sized values. Returns an array of
// { kind: 'same' | 'add' | 'del', a?: string, b?: string } ops in source order.

export function diffLines(aText, bText) {
  const a = String(aText ?? '').split('\n');
  const b = String(bText ?? '').split('\n');
  const m = a.length, n = b.length;
  // DP table of LCS lengths.
  const dp = Array.from({ length: m + 1 }, () => new Int32Array(n + 1));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const ops = [];
  let i = m, j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) { ops.push({ kind: 'same', a: a[i - 1], b: b[j - 1] }); i--; j--; }
    else if (dp[i - 1][j] >= dp[i][j - 1]) { ops.push({ kind: 'del', a: a[i - 1] }); i--; }
    else { ops.push({ kind: 'add', b: b[j - 1] }); j--; }
  }
  while (i > 0) { ops.push({ kind: 'del', a: a[i - 1] }); i--; }
  while (j > 0) { ops.push({ kind: 'add', b: b[j - 1] }); j--; }
  ops.reverse();
  return ops;
}
